// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

/**
 * D3 acceptance criterion 29 -- highlight resolver.
 *
 * Pure function that joins a caller-supplied
 * `ReadonlyMap<ident, VariableHighlight>` against the visible
 * `ViewElement`s and returns a per-highlight render list (one
 * `ResolvedHighlight` per matched ident).
 *
 * This is the *only* overlay-side file allowed to import the engine
 * `ViewElement` type.  Keeping the resolver in its own file lets the
 * actual renderer (`VariableHighlightOverlay.tsx`) stay engine-free,
 * which is what makes the C28 "without modifying model data"
 * contract trivially provable at the file-import level for the
 * renderer itself.
 *
 * The function performs only reads -- it never mutates the elements
 * passed in.  TypeScript's `ReadonlyArray` declaration prevents
 * accidental mutation in the body of the loop below.
 */

import {
  isNamedViewElement,
  ViewElement,
} from '@simlin/core/datamodel';

import { AuxRadius, ModuleHeight, ModuleWidth, StockHeight, StockWidth } from '../drawing/default';
import { ResolvedHighlight, VariableHighlight } from './highlights';

/**
 * Outer radius for the highlight ring around a given view element.
 * Picks a circle that fully encloses each kind's bounding shape with
 * a small margin so the ring sits visibly outside the variable's
 * own stroke.
 */
function outerRadiusFor(element: ViewElement): number {
  const RING_PADDING = 6;
  switch (element.type) {
    case 'stock':
      return Math.hypot(StockWidth / 2, StockHeight / 2) + RING_PADDING;
    case 'module':
      return Math.hypot(ModuleWidth / 2, ModuleHeight / 2) + RING_PADDING;
    case 'aux':
    case 'flow':
    case 'alias':
    default:
      return AuxRadius + RING_PADDING;
  }
}

/**
 * Resolve highlight metadata to render-ready coordinates.
 *
 * Idents in the highlights map that don't correspond to a visible
 * named variable are dropped silently -- a stale highlight from
 * outdated metadata should not crash the overlay; it should just
 * render nothing for that ident.
 *
 * The order of the returned array follows iteration order of
 * `elements`, so callers can rely on the natural z-stacking of the
 * displayed elements when picking which highlight paints on top.
 */
export function resolveHighlights(
  highlights: ReadonlyMap<string, VariableHighlight>,
  elements: ReadonlyArray<ViewElement>,
): ResolvedHighlight[] {
  if (highlights.size === 0) {
    return [];
  }
  const resolved: ResolvedHighlight[] = [];
  for (const element of elements) {
    if (!isNamedViewElement(element) || element.ident === undefined) {
      continue;
    }
    const highlight = highlights.get(element.ident);
    if (highlight === undefined) {
      continue;
    }
    const radius = outerRadiusFor(element);
    if (highlight.kind === 'badge') {
      // Badge sits at the top-right corner of the variable's outer
      // ring so it doesn't occlude the variable label below.
      const cornerOffset = radius * Math.SQRT1_2;
      resolved.push({
        uid: element.uid,
        kind: 'badge',
        cx: element.x + cornerOffset,
        cy: element.y - cornerOffset,
        radius: 8,
        badgeLabel: highlight.badgeLabel,
        color: highlight.color,
      });
      continue;
    }
    resolved.push({
      uid: element.uid,
      kind: highlight.kind,
      cx: element.x,
      cy: element.y,
      radius,
      color: highlight.color,
    });
  }
  return resolved;
}
